import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import React, { useCallback, useEffect, useRef, useState } from "react";
import theme from "./config/theme";

import { Doughnut } from "react-chartjs-2";
import classNames from "../../../utilities/ClassNames";
import calculateData from "./utils/calculateData";
import colorCodes from "./config/colorCodes";
import drawHiddenSplits from "./utils/drawSplits/drawHiddenSplits";
import drawSplits from "./utils/drawSplits/drawSplits";
import genThresholdRange from "./utils/genThresholdRange";
import genGaugeData from "./utils/gaugeData";
import getOptions from "./utils/getOptions";
import { GaugeProps, GaugeOptions } from "./domain/types";
import { gaugeConfig } from "./config/configurations";

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const Gauge: React.FC<GaugeProps> = ({ data, name, gaugeType, resultLabel, result, from }) => {
  const outOfThresholdRange = genThresholdRange(colorCodes);
  const [valueDataSet, setValueDataSet] = useState(outOfThresholdRange.value);
  const [valueBG, setValueBG] = useState(outOfThresholdRange.BG);

  const [operatingDataSet, setOperatingDataSet] = useState<number[]>([]);
  const [operatingBG, setOperatingBG] = useState<string[]>([]);

  const [total, setTotal] = useState(0);
  const [disabled, setDisable] = useState(false);
  const [inRange, setInRange] = useState(true);

  const { minValue, maxValue, minRange, maxRange, formattedValue, value, uom } = data;
  const [gradient, setGradient] = useState<string | null>(null);
  const chartRef = useRef<any>(null);
  const { titleSize, doughtnutContainerClasses, labelClasses, valueClasses, uomClasses } = gaugeConfig[gaugeType];

  useEffect(() => {
    if (data) {
      const calculatedData = calculateData(data, 100);

      setTotal(calculatedData.totalValue);
      setInRange(calculatedData.inRange);

      setOperatingDataSet(calculatedData.rangeSet.data);
      setOperatingBG(calculatedData.rangeSet.bg);

      if (!calculatedData.defaultDataSet) {
        setValueBG(calculatedData.valueSet.bg);
        setValueDataSet(calculatedData.valueSet.data);
      }

      setDisable(calculatedData.disable);
    }
  }, [data, drawSplits]);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart) {
      const ctx = chart?.ctx;
      const gradientSegment = ctx.createLinearGradient(0, 0, 250, 0);
      const gradient = { start: theme.primary, end: theme.secondary };

      if (!inRange) {
        gradient.start = colorCodes.orange;
        gradient.end = colorCodes.red;
      }
      gradientSegment.addColorStop(0, gradient.start);
      // gradientSegment.addColorStop(endBGData, gradient.end);

      setGradient(gradientSegment);
    }
  }, [valueBG, valueDataSet]);

  let bgGradient: (CanvasGradient | string | null)[] = [gradient];
  let borderBg: (CanvasGradient | string | null)[] = [gradient];
  if (valueBG.length === 2) {
    bgGradient = [gradient, valueBG[1]];
    borderBg = [gradient, "transparent"];
  } else if (valueBG.length === 1 && valueBG[0] === colorCodes.white) {
    bgGradient = valueBG;
    borderBg = ["transparent"];
  }

  const gaugeNeedle = useCallback(() => {
    return {
      id: `gaugeChart_${data.id}`,
      beforeDraw(chart: ChartJS<"doughnut", number[], unknown>, args: { cancelable: boolean }, options: GaugeOptions) {
        drawHiddenSplits({
          chart,
          args,
          options,
          values: {
            formattedValue,
            minRange,
            maxRange,
            minValue,
            maxValue,
            total,
          }
        });
      },
      afterDraw(chart: ChartJS<"doughnut", number[], unknown>, args: any, options: GaugeOptions) {
        drawSplits({
          chart,
          args,
          options,
          values: {
            formattedValue,
            minRange,
            maxRange,
            minValue,
            maxValue,
            total,
          },
          gaugeType
        });
      },
    };
  }, [total]);

  const gaugeData = genGaugeData({ valueDataSet, operatingBG, operatingDataSet, bgGradient, borderBg });
  const fontSize = 12;
  const options = getOptions({ fontSize, formattedValue });

  return (
    <div className={classNames(disabled ? "opacity-50" : "", "max-h-full max-w-full h-full w-full flex items-center justify-center")}>
      <div
        className="h-full max-h-full"
        style={{ aspectRatio: "1 / 0.85" }}
      >
        {total ? (
          operatingDataSet && (
            <div className="w-full h-full overflow-hidden relative flex flex-col justify-center items-center z-0">
              <div className={classNames("z-50 h-full w-full")}>
                <Doughnut
                  ref={chartRef}
                  id={`gaugeChart_${data.id}`}
                  key={`gaugeChart_${data.name}_${data.id}`}
                  data={gaugeData}
                  plugins={[gaugeNeedle()]}
                  options={options}
                  className={"w-full !h-full"}
                />
              </div>
              <div className={classNames(labelClasses, "mt-2 2xl:mt-4")}>
                <h4 className={classNames(valueClasses, "text-center opacity-80 absolute left-0 w-full")}>
                  <div className="h-min">
                    <div className={classNames("block mx-auto")}>
                      {data.uom === "%"
                        ? `${Math.trunc(parseInt(value?.toFixed(1).toString()))}%` || "No Value"
                        : value > 9 ? value?.toFixed(1) : value?.toFixed(2) || "No Value"}
                    </div>
                    {uom !== '%' && <div className={classNames(uomClasses, "h-min")}>{uom}</div>}
                  </div>
                </h4>
                <h4 className={classNames(titleSize, "text-center text-white font-bold",
                  from === "sidebar" ? "text-[10px] " : ""
                )}>
                  {name ? name : data.name}
                </h4>
                <div className="flex items-center ">
                  <h4 className={classNames("font-semibold",

                    from === "sidebar" ? "text-[16px] " : ""
                  )}>{resultLabel} <span className={classNames("font-semibold",
                    result === "69%" ? "text-[#ffb554]": "",
                    from === "sidebar" ? "text-[16px] text-[#65F4CD]" : ""
                  )} >{result}</span></h4>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-5">
            <p>Range not available</p>
            <h4
              className={classNames(
                "text-center font-weight-bold"
              )}
            >
              {formattedValue || "-"}
            </h4>
            <h4
              className={classNames(
                "text-center color-primary"
              )}
            >
              {name || data.name}
            </h4>
          </div>
        )}
      </div >
    </div >
  );
};

export default React.memo(Gauge);
